import { MDText } from 'i18n-react';

import { TickIcon } from '@motorway/mw-highway-code';

import { formatPrice } from 'Utilities/formatPrice';
import { applyCypressData } from 'Utilities/index';

import { useOfferSummary } from '../hooks/useOfferSummary';

import LocalTexts from './InfoList.texts.json';

import styles from './InfoList.styles.scss';

const LocalT = new MDText(LocalTexts);

interface PriceText {
  header: string;
  subtext: string;
}

export const OFFER_MORE_THAN_RESERVE_DELTA_THRESHOLD = 50; // £

const getPriceTexts = (acceptedBidValue: number | undefined, reservePrice: number | null | undefined): PriceText => {
  const offerReserveDelta = acceptedBidValue && reservePrice ? acceptedBidValue - reservePrice : 0;

  if (offerReserveDelta >= OFFER_MORE_THAN_RESERVE_DELTA_THRESHOLD) {
    // Scenario 1: The accepted bid execeeds the guide price by a threshold.

    return {
      header: LocalT.translate('listItems.price.header.overReserveAndThreshold') as string,
      subtext: LocalT.translate('listItems.price.subtext.overReserveAndThreshold', {
        delta: <strong>{formatPrice(offerReserveDelta)}</strong>,
      }) as string,
    };
  }

  if (offerReserveDelta > 0) {
    // Scenario 2: The accepted bid exceeds the guide price but not enough to reach the threshold.
    return {
      header: LocalT.translate('listItems.price.header.overReserve') as string,
      subtext: LocalT.translate('listItems.price.subtext.overReserve') as string,
    };
  }

  if (offerReserveDelta === 0) {
    // Scenario 3: The accepted bid meets the guide price exactly.
    return {
      header: LocalT.translate('listItems.price.header.meetsReserve') as string,
      subtext: LocalT.translate('listItems.price.subtext.meetsReserve') as string,
    };
  }

  // Scenario 4: The accepted bid price is below the guide price.
  return {
    header: LocalT.translate('listItems.price.header.belowReserve') as string,
    subtext: LocalT.translate('listItems.price.subtext.belowReserve') as string,
  };
};

const ListItem = ({
  header,
  icon = <TickIcon />,
  iconClass,
  subtext,
}: {
  header: string;
  icon?: React.ReactElement;
  iconClass?: string;
  subtext: string;
}) => (
  <li className={styles.listItem}>
    <div className={iconClass}>{icon}</div>
    <div className={styles.listItemContent}>
      <span className={styles.listItemHeader}>{header}</span>
      <span className={styles.listItemSubtext}>{subtext}</span>
    </div>
  </li>
);

export const InfoList = ({ closingBidValue }: { closingBidValue?: number }) => {
  const {
    offer: {
      acceptedBidDealerName,
      acceptedBidValue,
      keepPrivatePlate,
      reservePrice,
    },
  } = useOfferSummary();

  const finalOffer = acceptedBidValue ?? closingBidValue;

  const shouldShowPrivatePlateListItem = keepPrivatePlate;
  const shouldShowFasterPaymentsListItem = !keepPrivatePlate;
  const { header: priceHeader, subtext: priceSubtext } = getPriceTexts(finalOffer, reservePrice);

  const listItems = [
    {
      header: priceHeader,
      subtext: priceSubtext,
    },
    ...(shouldShowPrivatePlateListItem
      ? [
        {
          header: LocalT.translate('listItems.privatePlate.header') as string,
          subtext: LocalT.translate('listItems.privatePlate.subtext') as string,
        },
      ]
      : []),
    {
      header: LocalT.translate('listItems.dealer.header', {
        purchasingDealerName: acceptedBidDealerName,
      }) as string,
      subtext: LocalT.translate('listItems.dealer.subtext') as string,
    },
    {
      header: LocalT.translate('listItems.homeCollection.header') as string,
      subtext: LocalT.translate('listItems.homeCollection.subtext') as string,
    },
    ...(shouldShowFasterPaymentsListItem
      ? [
        {
          header: LocalT.translate('listItems.fasterPayment.header') as string,
          subtext: LocalT.translate('listItems.fasterPayment.subtext') as string,
        },
      ]
      : []),
  ];

  return (
    <div className={styles.infoList}>
      <div className={styles.slogan} {...applyCypressData('soldSlogan')}>{LocalT.translate('soldSlogan')}</div>
      {finalOffer && (
        <div className={styles.salePrice} {...applyCypressData('acceptedSalePrice')}>{formatPrice(finalOffer)}</div>
      )}
      <ul className={styles.list}>
        {listItems.map((props) => (
          <ListItem key={props.header} {...props} />
        ))}
      </ul>
    </div>
  );
};
